import { Button, Dialog, DialogBody, DialogContent, DialogFooter, DialogTrigger } from '@civilgrid/ui/components';
import { DateTime } from 'luxon';
import type React from 'react';
import { useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';
import { setUserToApproved as apiSetUserToApproved } from '~/api/internal';
import { useQuery } from '~/common/routerUtils';
import type { User } from '~/common/types';
import ControlledCheckbox from '~/components2/forms/controlled/ControlledCheckbox';
import Form from '~/components2/forms/Form';
import { Notify } from '~/components2/Notify';

interface Props {
  users: User[];
  refetch: () => void;
}

const config: {
  key: keyof User;
  label: string;
  getValue?: (u: User) => React.ReactNode;
}[] = [
  { key: 'email', label: 'Email' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'id', label: 'ID' },
  {
    key: 'memberships',
    label: 'Organization(s)',
    getValue: (u) =>
      u.memberships?.length
        ? u.memberships
            .map((m) => `${m.organizationName} — ${m.role}${m.status === 'suspended' ? ' (suspended)' : ''}`)
            .join(', ')
        : '—',
  },
  { key: 'status', label: 'Status' },
  {
    key: 'createdAt',
    label: 'Created At',
    getValue: (u) => {
      return DateTime.fromJSDate(new Date(u.createdAt)).toFormat('h:mm a M/d/yyyy');
    },
  },
];

export default function UserDetails({ users, refetch }: Props) {
  const query = useQuery();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const current = useMemo(() => {
    const id = query.get('userId');
    if (id) {
      const user = users.find((u) => u.id === id);
      if (user) {
        return (
          <div className="w-full space-y-4">
            <div className="flex w-full flex-col">
              {config.map((item) => {
                return (
                  <div
                    key={`user-${user.id}-property-${item.key}`}
                    className="flex w-full items-start justify-between space-x-2"
                  >
                    <div className="w-1/3">{item.label}</div>
                    <div className="w-2/3">{item.getValue?.(user) || (user[item.key] as string)}</div>
                  </div>
                );
              })}
            </div>

            <CheckVerificationDialog user={user} refetch={refetch} />
          </div>
        );
      }
    }

    return <div>Select a User from the table.</div>;
  }, [query]);

  return (
    <div className="space-y-4">
      <div className="text-base font-medium">Selected User</div>
      {current}
    </div>
  );
}

function CheckVerificationDialog({ user, refetch }: { user: User; refetch: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="unapproved-user-check-verification-trigger">Check Verification</Button>
      </DialogTrigger>
      <DialogContent
        title="Confirm Persona Verification"
        className="z-[110] w-auto min-w-min max-w-[75vw]"
        overlayClassName="z-[100]"
        data-testid="unapproved-user-check-verification-dialog"
      >
        <Content user={user} close={() => setOpen(false)} refetch={refetch} />
      </DialogContent>
    </Dialog>
  );
}

interface ContentProps {
  user: User;
  close: () => void;
  refetch: () => void;
}

type TFormValues = { confirmed: boolean };

const Content = ({ user, close, refetch }: ContentProps) => {
  const [approvalState, queryApproval] = useAsyncFn(() => apiSetUserToApproved(user.id), [user.id]);

  const form = useForm<TFormValues>({ defaultValues: { confirmed: false } });

  const onSubmit: SubmitHandler<TFormValues> = async (data) => {
    if (!data.confirmed) {
      Notify.error({ message: 'You must confirm the report first!' });
      return;
    }

    try {
      await queryApproval();
      Notify.info({ message: `${user.email} approved!` });
      close();
      refetch();
    } catch (e) {
      console.error('Error approving: ', e);
    }
  };

  if (approvalState.error) {
    return <DialogBody className="text-sm">An error occurred during approval.</DialogBody>;
  }

  const inquiryId = user.verificationApprovalWebhookReceived?.[0]?.inquiryId;

  return (
    <Form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
      <DialogBody className="text-sm">
        <div className="flex min-w-[24rem] flex-col items-start space-y-4">
          <div>
            User: <span>{user.email}</span>
          </div>

          <a href={`https://app.withpersona.com/dashboard/inquiries/${inquiryId}`} target="_blank">
            View Report
          </a>

          <div className="flex items-center space-x-2">
            <ControlledCheckbox control={form.control} name="confirmed" label="I have reviewed the report" required />
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button type="submit" disabled={!form.formState.isValid} data-testid="unapproved-user-approve">
          Approve
        </Button>
      </DialogFooter>
    </Form>
  );
};
